import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Flame, Sparkles, Loader2, RefreshCw, ExternalLink, MessageSquare, AlertTriangle, Lightbulb, Hash } from 'lucide-react';
import api from '../services/api';
import EnginePill from '../components/EnginePill';
import AIChat from '../components/AIChat';

const RedditInsights = () => {
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('');
  const [citations, setCitations] = useState(null);
  const [citationsLoading, setCitationsLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const resp = await api.get('/websites');
        setWebsites(resp.data.websites);
        if (resp.data.websites?.length === 1) {
          setSelectedWebsite(resp.data.websites[0].id);
        }
      } catch (err) {
        setError('Failed to fetch websites');
      }
    };
    fetchWebsites();
  }, []);

  useEffect(() => {
    if (!selectedWebsite) {
      setCitations(null);
      setInsights(null);
      return;
    }
    const fetchCitations = async () => {
      setCitationsLoading(true);
      setError('');
      setInsights(null);
      try {
        const resp = await api.get(`/reddit-insights/${selectedWebsite}/citations`);
        setCitations(resp.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load Reddit citations');
      } finally {
        setCitationsLoading(false);
      }
    };
    fetchCitations();
  }, [selectedWebsite]);

  const runAnalysis = async (force = false) => {
    setAnalysisLoading(true);
    setError('');
    try {
      const resp = await api.post(`/reddit-insights/${selectedWebsite}/analyze`, { force, maxThreads: 20 });
      setInsights(resp.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to analyze Reddit threads');
    } finally {
      setAnalysisLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight">Reddit Insights</h1>
            <EnginePill />
          </div>
          <p className="text-muted-foreground mt-2">
            What Reddit threads ChatGPT cites in your space — with the actual posts and top comments analysed by AI, not just the snippet ChatGPT showed.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5" />
              Select a website
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
              <SelectTrigger className="max-w-md">
                <SelectValue placeholder="Pick a website" />
              </SelectTrigger>
              <SelectContent>
                {websites.map(w => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.brandName} ({w.domain})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {selectedWebsite && citations && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Reddit threads in your citations
                    </CardTitle>
                    <CardDescription>
                      {citations.threads.length} unique thread{citations.threads.length === 1 ? '' : 's'} across {citations.totalRedditCitations} Reddit citation{citations.totalRedditCitations === 1 ? '' : 's'}.
                    </CardDescription>
                  </div>
                  {citations.threads.length > 0 && (
                    <Button
                      onClick={() => runAnalysis(false)}
                      disabled={analysisLoading}
                    >
                      {analysisLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading threads…</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" /> Run AI analysis</>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {citationsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : citations.threads.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No Reddit citations yet. Once ChatGPT cites Reddit threads when answering your tracked keywords, they'll show up here.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {citations.threads.slice(0, 15).map(t => (
                      <div key={t.url} className="rounded-md border p-3 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <a
                              href={t.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-sm hover:text-primary transition-colors line-clamp-2"
                            >
                              {t.title || t.url}
                            </a>
                            {t.snippet && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.snippet}</p>
                            )}
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {t.keywords.slice(0, 4).map(k => (
                                <Badge key={k.id} variant="outline" className="text-[10px]">{k.keyword}</Badge>
                              ))}
                              {t.keywords.length > 4 && (
                                <span className="text-[10px] text-muted-foreground">+{t.keywords.length - 4}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge variant="secondary">{t.totalCitations} cite{t.totalCitations === 1 ? '' : 's'}</Badge>
                            <a
                              href={t.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-primary"
                              aria-label="Open Reddit thread"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                    {citations.threads.length > 15 && (
                      <p className="text-xs text-muted-foreground italic pt-1">
                        + {citations.threads.length - 15} more thread{citations.threads.length - 15 === 1 ? '' : 's'} (analysis runs on top {Math.min(citations.threads.length, 20)})
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {analysisLoading && !insights && (
              <Card>
                <CardContent className="py-8 flex flex-col items-center gap-3 text-sm text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <p>Fetching Reddit threads and reading the discussions… this takes ~30 seconds.</p>
                </CardContent>
              </Card>
            )}

            {insights && (
              <Card className="border-primary/30 bg-gradient-to-br from-background to-primary/[0.03]">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        AI Analysis
                        {insights.cached && (
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded">cached</span>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Read {insights.threadsAnalysed} of {insights.threadsRequested} threads (post + top comments).
                      </CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => runAnalysis(true)}
                      disabled={analysisLoading}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="rounded-lg bg-muted/50 p-4">
                    <p className="text-sm leading-relaxed">{insights.analysis.summary}</p>
                  </div>

                  {insights.analysis.subredditMix?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                        <Hash className="h-4 w-4" />
                        Where the conversation is happening
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {insights.analysis.subredditMix.map((s, i) => (
                          <Tooltip key={i}>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="cursor-help">
                                {s.subreddit} · {s.threadCount}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{s.tone}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  )}

                  {insights.analysis.themes?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">What people are talking about</h3>
                      <div className="space-y-2">
                        {insights.analysis.themes.map((t, i) => (
                          <div key={i} className="rounded-md border bg-background p-3">
                            <p className="font-semibold text-sm">{t.theme}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{t.evidence}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {insights.analysis.painPoints?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        Pain points to act on
                      </h3>
                      <ul className="space-y-1.5 text-sm">
                        {insights.analysis.painPoints.map((p, i) => (
                          <li key={i} className="flex gap-2"><span className="text-orange-600 mt-0.5">▸</span><span>{p}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {insights.analysis.brandMentions?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Brand mentions in threads</h3>
                      <div className="space-y-2">
                        {insights.analysis.brandMentions.map((m, i) => (
                          <div key={i} className="text-sm">
                            <span className="font-semibold">{m.brand}:</span>{' '}
                            <span className="text-muted-foreground">{m.context}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {insights.analysis.engagementOpportunities?.length > 0 && (
                    <div className="rounded-lg border-2 border-primary/30 bg-primary/[0.04] p-4">
                      <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                        <Lightbulb className="h-4 w-4 text-primary" />
                        Where you could helpfully engage
                      </h3>
                      <ul className="space-y-2 text-sm">
                        {insights.analysis.engagementOpportunities.map((o, i) => (
                          <li key={i}>
                            <span className="font-semibold">{o.thread}:</span>{' '}
                            <span>{o.why}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {selectedWebsite && <AIChat websiteId={selectedWebsite} />}
      </div>
    </TooltipProvider>
  );
};

export default RedditInsights;
